import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import TiptapMenuBar from './TiptapMenuBar';
import {
  StyledEditorContainer,
  StyledEditorContent,
  LinkDialogOverlay,
  LinkDialogFieldContainer,
  LinkDialogLabel,
  LinkDialogInput,
  LinkDialogButtonContainer,
  LinkDialogCancelButton,
  LinkDialogSaveButton,
  LinkDialogTitle,
} from './TiptapEditorCSS';
import TextAlign from './TiptapTextAlignExtension';

const TiptapEditor = ({ value, onUpdate, editable }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Heading,
      BulletList,
      OrderedList,
      ListItem,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          class: 'custom-link',
        },
      }),
    ],
    content: value ?? '',
    onUpdate: ({ editor }) => {
      onUpdate && onUpdate(editor.getHTML());
    },
    autofocus: true,
    editable,
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, editable]); // we don't run effect each time the value change

  // Functions for handling links
  // Function to check if text is selected in the editor
  const hasTextSelected = () => {
    if (!editor) return false;
    return editor.state.selection.content().size > 0;
  };

  // Function to open the link dialog
  const openLinkDialog = () => {
    // If we have text selected, use it as the link text
    if (hasTextSelected()) {
      const selectedText = editor.state.doc.textBetween(
        editor.state.selection.from,
        editor.state.selection.to,
        ''
      );
      setLinkText(selectedText);
    } else {
      setLinkText('');
    }
    if (editor.isActive('link')) {
      // If editing an existing link, get its attributes
      const attrs = editor.getAttributes('link');
      setLinkUrl(attrs.href || '');
    }

    setLinkDialogOpen(true);
  };

  // Function for setting links
  const setLink = (url, text) => {
    // Cancelled or empty URL
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Add https:// if no protocol is specified
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }
    let trgText = text;
    if (!trgText) {
      trgText = url;
    }
    // If we have text and no selection, insert it first
    if (trgText) {
      editor.chain().focus().insertContent(trgText).run();
      editor
        .chain()
        .focus()
        .setTextSelection({
          from: editor.state.selection.from - trgText.length,
          to: editor.state.selection.from,
        })
        .run();
    }

    // Update the link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  // Function to handle the link dialog submission
  const handleLinkDialogSubmit = (e) => {
    e.preventDefault();
    setLink(linkUrl, linkText);
    setLinkDialogOpen(false);
    setLinkUrl('');
    setLinkText('');
  };

  return (
    <StyledEditorContainer $isFullScreen={isFullScreen}>
      <TiptapMenuBar
        editor={editor}
        toggleFullScreen={() => setIsFullScreen(!isFullScreen)}
        isFullScreen={isFullScreen}
        openLinkDialog={openLinkDialog}
      />
      <StyledEditorContent $isFullScreen={isFullScreen} editor={editor} />

      {linkDialogOpen && (
        <LinkDialogOverlay>
          <form onSubmit={handleLinkDialogSubmit}>
            <LinkDialogTitle>Insert Link</LinkDialogTitle>

            <LinkDialogFieldContainer>
              <LinkDialogLabel htmlFor="linkUrl">URL:</LinkDialogLabel>
              <LinkDialogInput
                id="linkUrl"
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                autoFocus
              />
            </LinkDialogFieldContainer>

            <LinkDialogFieldContainer $marginBottom="15px">
              <LinkDialogLabel htmlFor="linkText">
                Text:{' '}
                {hasTextSelected() && (
                  <span style={{ fontSize: '12px', color: 'gray' }}>
                    (selected text will be used if empty)
                  </span>
                )}
              </LinkDialogLabel>
              <LinkDialogInput
                id="linkText"
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Link text"
              />
            </LinkDialogFieldContainer>

            <LinkDialogButtonContainer>
              <LinkDialogCancelButton
                type="button"
                onClick={() => {
                  setLinkDialogOpen(false);
                  setLinkUrl('');
                  setLinkText('');
                }}
              >
                Cancel
              </LinkDialogCancelButton>
              <LinkDialogSaveButton type="submit">Save</LinkDialogSaveButton>
            </LinkDialogButtonContainer>
          </form>
        </LinkDialogOverlay>
      )}
    </StyledEditorContainer>
  );
};

export default TiptapEditor;
